/**
 * lutParameterization — CPU reference for the Hillaire 2020 LUT coordinate
 * mappings, and helpers to construct the matching TSL shader nodes.
 *
 * Two parameterizations are implemented here:
 *
 *   (1) **Transmittance LUT** (256×64)
 *       UV.x = function(cosViewZenith µ, radius r)  — Bruneton 2017 remap
 *       UV.y = function(radius r)
 *
 *   (2) **Sky-view LUT** (192×108)
 *       UV.x = squared-remap(azimuth to sun)
 *       UV.y = non-linear zenith remap (two halves with squared spacing
 *              around the horizon for detail preservation)
 *
 * These are CPU reference functions — the GPU implementation uses the same
 * math inside TSL `Fn` blocks. Unit tests validate that endpoints round-trip
 * and that the horizon line (uv.y = 0.5 in sky-view) maps to cosTheta = 0.
 *
 * References:
 *   - Hillaire 2020, section 5 (sky-view non-linear parameterization)
 *   - Bruneton 2017 "Precomputed Atmospheric Scattering" (transmittance remap)
 */

import type { AtmosphereConfig } from './AtmosphereConfig.js';

/**
 * Map (altitude, cosViewZenith) → UV in the transmittance LUT.
 *
 * Bruneton 2017 "Precomputed Atmospheric Scattering" remap:
 *   H     = sqrt(Rt² − Rg²)       — half-chord of the atmosphere shell
 *   ρ     = sqrt(r² − Rg²)        — horizontal distance from ground at radius r
 *   x_r   = ρ / H                  — altitude axis (uv.y)
 *   d     = max(0, sqrt(d*(-d²)))  — ray-to-top-of-atm distance via quadratic
 *   x_mu  = non-linear mapping of d
 *
 * The exact form used here matches the JolifantoBambla WebGPU port and the
 * UE reference: compute a "horizon distance" at the current altitude, then
 * map the ray distance to that horizon non-linearly.
 */
export function transmittanceUV(
  cfg: AtmosphereConfig,
  radius: number,     // distance from planet center (km)
  cosViewZenith: number,  // µ = dot(view, up) at the sample point
): { u: number; v: number } {
  const Rg = cfg.bottomRadius;
  const Rt = cfg.topRadius;
  const H = Math.sqrt(Rt * Rt - Rg * Rg);
  // Altitude parameterization (uv.y)
  const rho = Math.sqrt(Math.max(0, radius * radius - Rg * Rg));
  const x_r = rho / H;

  // Ray-distance parameterization (uv.x)
  const discriminant = radius * radius * (cosViewZenith * cosViewZenith - 1.0)
                     + Rt * Rt;
  // distance to top of atmosphere along the view ray
  const d = Math.max(0, -radius * cosViewZenith + Math.sqrt(Math.max(0, discriminant)));
  const d_min = Rt - radius;
  const d_max = rho + H;
  const x_mu = (d_max - d_min) !== 0 ? (d - d_min) / (d_max - d_min) : 0;

  return { u: clamp01(x_mu), v: clamp01(x_r) };
}

/** Inverse of transmittanceUV — given UV, recover (radius, cosViewZenith). */
export function transmittanceUVInverse(
  cfg: AtmosphereConfig, u: number, v: number,
): { radius: number; cosViewZenith: number } {
  const Rg = cfg.bottomRadius;
  const Rt = cfg.topRadius;
  const H = Math.sqrt(Rt * Rt - Rg * Rg);

  const x_r = clamp01(v);
  const rho = x_r * H;
  const radius = Math.sqrt(rho * rho + Rg * Rg);

  const d_min = Rt - radius;
  const d_max = rho + H;
  const d = d_min + clamp01(u) * (d_max - d_min);

  // Recover cosTheta = µ from d at radius r (top-of-atm intersection)
  let cosViewZenith = d === 0
    ? 1.0
    : (H * H - rho * rho - d * d) / (2.0 * radius * d);
  cosViewZenith = clamp(cosViewZenith, -1.0, 1.0);
  return { radius, cosViewZenith };
}

// ─── Sky-View LUT ────────────────────────────────────────────────────────────

/**
 * Map (azimuth, view zenith angle, viewHeight) → UV in the sky-view LUT.
 *
 * The zenith half uses Hillaire 2020's "squared" remap around the horizon
 * line. Squaring concentrates samples near the horizon (where the colour
 * gradient is steepest). UV.y = 0 is zenith, 0.5 is exactly the horizon,
 * and 1.0 is ground.
 *
 * Azimuth uses a simpler squared remap symmetric around the sun direction.
 */
export function skyViewUV(
  cfg: AtmosphereConfig,
  viewHeight: number,      // radius from planet center (km), >= bottomRadius
  cosViewZenith: number,   // dot(viewDir, up)
  cosLightView: number,    // dot(viewDir, sunDir) — sun-relative azimuth
): { u: number; v: number } {
  const Rg = cfg.bottomRadius;
  // Height-above-ground clamp to at least 1 m to avoid sqrt(0) edge case
  const r = Math.max(viewHeight, Rg + 1e-3);
  const vHoriz = Math.sqrt(Math.max(0, r * r - Rg * Rg));
  // ground-to-horizon angle β (from the camera's perspective, looking DOWN
  // to the horizon), measured from zenith
  const groundToHorizon = Math.acos(vHoriz / r);
  const zenithToHorizon = Math.PI - groundToHorizon;

  const theta = Math.acos(clamp(cosViewZenith, -1, 1));
  let v: number;
  if (theta < zenithToHorizon) {
    // Upper half: UV.y ∈ [0, 0.5). Non-linear via sqrt on the "coord"
    const coord = 1.0 - theta / zenithToHorizon;
    const unitCoord = 1.0 - Math.sqrt(Math.max(0, coord));
    v = 0.5 * unitCoord;
  } else {
    // Lower half: UV.y ∈ [0.5, 1]
    const coord = (theta - zenithToHorizon) / groundToHorizon;
    const unitCoord = Math.sqrt(Math.max(0, coord));
    v = 0.5 + 0.5 * unitCoord;
  }

  // Azimuth: squared remap centred on sun
  //   cosLightView = 1  → sun direction → u = 0.5
  //   cosLightView = -1 → anti-sun      → u = 0 or 1 (edges)
  // Hillaire's form:
  //   u = 0.5 + 0.5 * sign(cosLightView) * sqrt((1 - cosLightView) / 2)
  // Simpler symmetric form used here:
  const cosClamped = clamp(cosLightView, -1, 1);
  const u = 0.5 + 0.5 * Math.sign(cosClamped) *
                  Math.sqrt(Math.abs(cosClamped));

  return { u: clamp01(u), v: clamp01(v) };
}

/** Inverse of skyViewUV — recover (cosViewZenith, cosLightView). */
export function skyViewUVInverse(
  cfg: AtmosphereConfig,
  viewHeight: number,
  u: number,
  v: number,
): { cosViewZenith: number; cosLightView: number } {
  const Rg = cfg.bottomRadius;
  const r = Math.max(viewHeight, Rg + 1e-3);
  const vHoriz = Math.sqrt(Math.max(0, r * r - Rg * Rg));
  const groundToHorizon = Math.acos(vHoriz / r);
  const zenithToHorizon = Math.PI - groundToHorizon;

  let theta: number;
  if (v < 0.5) {
    // Upper half: inverse of { coord = 1 - θ/Z; unit = 1 - sqrt(coord); v = unit/2 }
    // unit = 2v, coord = (1 - unit)², θ = Z * (1 - coord)
    const unit = 2.0 * v;
    const coord = (1.0 - unit) * (1.0 - unit);
    theta = zenithToHorizon * (1.0 - coord);
  } else {
    // Lower half: inverse of { coord = (θ - Z)/G; unit = sqrt(coord); v = 0.5 + unit/2 }
    const unit = (v - 0.5) * 2.0;
    const coord = unit * unit;
    theta = zenithToHorizon + groundToHorizon * coord;
  }
  const cosViewZenith = Math.cos(theta);

  // Invert azimuth remap
  const s = (u - 0.5) * 2.0;               // ∈ [-1, 1]
  const cosLightView = Math.sign(s) * s * s;

  return { cosViewZenith, cosLightView };
}

// ─── Utilities ──────────────────────────────────────────────────────────────

function clamp01(x: number): number { return x < 0 ? 0 : (x > 1 ? 1 : x); }
function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : (x > hi ? hi : x);
}

/**
 * Ray-sphere intersection used to find the "top of atmosphere" along a view
 * ray from `origin` in direction `dir`. Returns the POSITIVE parameter t ≥ 0
 * where `origin + dir*t` lies on the sphere of `radius`, or -1 on no hit
 * (should never happen when origin is inside the atmosphere shell).
 */
export function rayAtmosphereIntersect(
  originRadius: number,     // distance from planet center
  cosViewZenith: number,    // dot(dir, up) at origin
  sphereRadius: number,     // radius of the target shell
): number {
  // Quadratic: |O + t·D|² = R²
  // With |O| = r, dot(O, D) = r·cosViewZenith
  const b = originRadius * cosViewZenith;
  const c = originRadius * originRadius - sphereRadius * sphereRadius;
  const disc = b * b - c;
  if (disc < 0) return -1;
  const sqrtDisc = Math.sqrt(disc);
  // Return the farthest positive root (= atmosphere exit distance for a
  // ray from inside the shell).
  const t = -b + sqrtDisc;
  return t >= 0 ? t : -1;
}
